import type { SearchExperiment, SearchRequest } from '@kimuno/api-sdk/lib/types/requests/Common';
import type { SearchResponseData } from '@kimuno/api-sdk/lib/types/responses/Common';
import type {
	ContentType,
	CourseMindItemSearchResult,
} from '@kimuno/api-sdk/lib/types/responses/MindItem';
import config from 'config';
import _ from 'lodash';

import { defaultSearchFilters } from '../constants';
import services from '../services';
import type { Pagination, SearchFilters } from '../types/search';
import { getCurrentSearchId } from '../utils/ampli';
import { hasNext } from '../utils/pagination';
import queryToSearchFilters from '../utils/queryToSearchFilters';
import { isSchoolSubdomain } from '../utils/subdomain';
import { AsyncActionStatus, type ThunkAction } from './common.actionTypes';
import {
	FETCH_TEACHING_FORMATS,
	FETCH_WORKLOADS,
	type FetchTeachingFormatsAction,
	type FetchWorkloadsAction,
	SEARCH_COURSE_MIND_ITEMS,
	SEARCH_MIND_ITEMS,
	SEARCH_PRODUCTS,
	SEARCH_TOPICS,
	SEARCH_UNIFIED,
	SEARCH_VIDEO_MIND_ITEMS,
	SET_RESULTS_SEARCH_ID,
	type SearchCourseMindItemsAction,
	type SearchMindItemsAction,
	type SearchProductsAction,
	type SearchTopicsAction,
	type SearchUnifiedAction,
	type SearchVideoMindItemsAction,
	type SetResultsSearchIdAction,
} from './search.actionTypes';

export const SEARCH_EXPERIMENT_DEMOTE_READ_ONLY = 'demote-read-only' as unknown as SearchExperiment;
export const SEARCH_EXPERIMENT_DEEP_LINKING = 'deep-linking' as unknown as SearchExperiment;

export interface SearchOptions {
	clearPreviousResults?: boolean;
	excludePublishers?: boolean;
	populateProducts?: boolean;
	scoreAggregationMethod?: string;
	experiments?: SearchExperiment[];
	// SEAR-460: when true, kimuno filters/boosts subject/schoolType/classYear on
	// AI-tag fields instead of CP fields (APIv3 forwards it as a plain query param).
	useAiFilters?: boolean;
	pagination?: Pagination;
}

// Aborted requests are an intentional cancellation, not a failure: skip dispatching
// *.ERROR so the UI doesn't surface them as search failures. The next caller-driven
// dispatch (the reason we aborted) immediately fires *.START and resets loading state.
const isAbortError = (error: unknown): boolean =>
	error instanceof DOMException && error.name === 'AbortError';

export function searchMindItems(
	filters: SearchFilters,
	searchOptions: SearchOptions = { clearPreviousResults: false },
	abortSignal?: AbortSignal
): ThunkAction<SearchMindItemsAction> {
	return async (dispatch, getState) => {
		let request: SearchRequest;
		let payload: { filters: SearchFilters; pagination: Pagination };
		const pagination = searchOptions.pagination || getState().search.mindItems.pagination;

		if (shouldFetchNextResults(filters, getState().search.searchFilters, searchOptions)) {
			if (!hasNext(getState().search.mindItems.pagination)) {
				return;
			}

			request = {
				...getState().search.searchFilters,
				offset: pagination.offset,
				limit: pagination.limit,
				excludePublishers: searchOptions.excludePublishers,
				scoreAggregationMethod: searchOptions.scoreAggregationMethod,
				populateProducts: searchOptions.populateProducts,
				experiments: searchOptions.experiments,
				useAiFilters: searchOptions.useAiFilters,
			};
			payload = {
				filters: getState().search.searchFilters || defaultSearchFilters,
				pagination,
			};
		} else {
			pagination.offset = 0;
			request = {
				...filters,
				offset: pagination.offset,
				limit: pagination.limit,
				excludePublishers: searchOptions.excludePublishers,
				scoreAggregationMethod: searchOptions.scoreAggregationMethod,
				populateProducts: searchOptions.populateProducts,
				experiments: searchOptions.experiments,
				useAiFilters: searchOptions.useAiFilters,
			};
			payload = { filters, pagination };
		}

		dispatch({
			type: SEARCH_MIND_ITEMS.START,
			payload: { ...payload, searchId: getCurrentSearchId() },
		});

		// Don't fetch video mind items if it's on the school subdomain
		if (isSchoolSubdomain(window.location.hostname)) {
			request.contentType = ['document'] as ContentType[];
		}

		//exclude MindItems type COURSE from every default search query
		if (!request.contentType) {
			request.contentType = defaultSearchFilters.contentType;
		}

		services.mindItem
			.search(request, abortSignal)
			.then((result) => {
				dispatch({ type: SEARCH_MIND_ITEMS.SUCCESS, result, payload });
			})
			.catch((error) => {
				if (isAbortError(error)) return;
				dispatch({ error, type: SEARCH_MIND_ITEMS.ERROR, payload });
			});
	};
}

export function searchVideoMindItems(
	filters: SearchFilters,
	searchOptions: SearchOptions = { clearPreviousResults: false },
	abortSignal?: AbortSignal
): ThunkAction<SearchVideoMindItemsAction> {
	return async (dispatch, getState) => {
		let request: SearchRequest;
		let payload: { filters: SearchFilters; pagination: Pagination };
		const pagination = searchOptions.pagination || getState().search.videoMindItems.pagination;

		if (shouldFetchNextResults(filters, getState().search.searchFilters, searchOptions)) {
			if (!hasNext(getState().search.videoMindItems.pagination)) {
				return;
			}

			request = {
				...getState().search.searchFilters,
				offset: pagination.offset,
				limit: pagination.limit,
				excludePublishers: searchOptions.excludePublishers,
				scoreAggregationMethod: searchOptions.scoreAggregationMethod,
				populateProducts: searchOptions.populateProducts,
				experiments: searchOptions.experiments,
				useAiFilters: searchOptions.useAiFilters,
			};
			payload = {
				filters: getState().search.searchFilters || defaultSearchFilters,
				pagination,
			};
		} else {
			pagination.offset = 0;
			request = {
				...filters,
				offset: pagination.offset,
				limit: pagination.limit,
				excludePublishers: searchOptions.excludePublishers,
				scoreAggregationMethod: searchOptions.scoreAggregationMethod,
				populateProducts: searchOptions.populateProducts,
				experiments: searchOptions.experiments,
				useAiFilters: searchOptions.useAiFilters,
			};
			payload = { filters, pagination };
		}

		dispatch({
			type: SEARCH_VIDEO_MIND_ITEMS.START,
			payload: { ...payload, searchId: getCurrentSearchId() },
		});

		// Fetch only video mind items
		request.contentType = ['video'] as ContentType[];

		// If the user is on the school subdomain, only fetch videos from non-vimeo-publishers
		let shouldSendRequest = true;
		if (isSchoolSubdomain(window.location.hostname)) {
			if (request.publisher && request.publisher.length > 0) {
				const validSelectedPublishers = request.publisher.filter((publisher) =>
					config.schoolsApp.nonVimeoVideoPublishers.includes(publisher)
				);
				if (validSelectedPublishers.length > 0) {
					request.publisher = validSelectedPublishers;
				} else {
					shouldSendRequest = false;
				}
			} else {
				request.publisher = config.schoolsApp.nonVimeoVideoPublishers;
			}
		}

		if (shouldSendRequest) {
			services.mindItem
				.search(request, abortSignal)
				.then((result) => {
					dispatch({ type: SEARCH_VIDEO_MIND_ITEMS.SUCCESS, result, payload });
				})
				.catch((error) => {
					if (isAbortError(error)) return;
					dispatch({ error, type: SEARCH_VIDEO_MIND_ITEMS.ERROR, payload });
				});
		} else {
			const emptySearchResult = {
				data: [],
				meta: {
					pagination: {
						total: 0,
					},
				},
			};
			dispatch({ type: SEARCH_VIDEO_MIND_ITEMS.SUCCESS, result: emptySearchResult, payload });
		}
	};
}

export function searchCourseMindItems(
	filters: SearchFilters,
	searchOptions: SearchOptions = { clearPreviousResults: false },
	abortSignal?: AbortSignal
): ThunkAction<SearchCourseMindItemsAction> {
	return async (dispatch, getState) => {
		let request: SearchRequest;
		let payload: { filters: SearchFilters; pagination: Pagination };
		const pagination = searchOptions.pagination || getState().search.courseMindItems.pagination;

		if (shouldFetchNextResults(filters, getState().search.courseSearchFilters, searchOptions)) {
			if (!hasNext(getState().search.courseMindItems.pagination)) {
				return;
			}

			request = {
				...getState().search.courseSearchFilters,
				offset: pagination.offset,
				limit: pagination.limit,
				excludePublishers: searchOptions.excludePublishers,
				scoreAggregationMethod: searchOptions.scoreAggregationMethod,
			};
			payload = {
				filters: getState().search.courseSearchFilters || queryToSearchFilters(),
				pagination,
			};
		} else {
			pagination.offset = 0;
			request = {
				...filters,
				offset: pagination.offset,
				limit: pagination.limit,
				excludePublishers: searchOptions.excludePublishers,
				scoreAggregationMethod: searchOptions.scoreAggregationMethod,
			};
			payload = { filters, pagination };
		}

		dispatch({
			type: SEARCH_COURSE_MIND_ITEMS.START,
			payload: { ...payload, searchId: getCurrentSearchId() },
		});

		// Fetch only course mind items
		request.contentType = ['course'] as ContentType[];

		services.mindItem
			.search(request, abortSignal)
			.then((result) => {
				dispatch({
					type: SEARCH_COURSE_MIND_ITEMS.SUCCESS,
					result: result as SearchResponseData<CourseMindItemSearchResult>,
					payload,
				});
			})
			.catch((error) => {
				if (isAbortError(error)) return;
				dispatch({ error, type: SEARCH_COURSE_MIND_ITEMS.ERROR, payload });
			});
	};
}

export function searchProducts(
	filters: SearchFilters,
	searchOptions: SearchOptions = { clearPreviousResults: false },
	abortSignal?: AbortSignal
): ThunkAction<SearchProductsAction> {
	return async (dispatch, getState) => {
		let request: SearchRequest;
		let payload: { filters: SearchFilters; pagination: Pagination };
		const pagination = searchOptions.pagination || getState().search.products.pagination;

		if (shouldFetchNextResults(filters, getState().search.searchFilters, searchOptions)) {
			if (!hasNext(getState().search.products.pagination)) {
				return;
			}
			request = {
				...getState().search.searchFilters,
				offset: pagination.offset,
				limit: pagination.limit,
				excludePublishers: searchOptions.excludePublishers,
				scoreAggregationMethod: searchOptions.scoreAggregationMethod,
				experiments: searchOptions.experiments,
				useAiFilters: searchOptions.useAiFilters,
			};
			payload = {
				filters: getState().search.searchFilters || defaultSearchFilters,
				pagination,
			};
		} else {
			pagination.offset = 0;
			request = {
				...filters,
				offset: pagination.offset,
				limit: pagination.limit,
				excludePublishers: searchOptions.excludePublishers,
				scoreAggregationMethod: searchOptions.scoreAggregationMethod,
				experiments: searchOptions.experiments,
				useAiFilters: searchOptions.useAiFilters,
			};
			payload = { filters, pagination };
		}

		dispatch({
			type: SEARCH_PRODUCTS.START,
			payload: { ...payload, searchId: getCurrentSearchId() },
		});

		services.product
			.search(request, abortSignal)
			.then((result) => {
				dispatch({ type: SEARCH_PRODUCTS.SUCCESS, result, payload });
			})
			.catch((error) => {
				if (isAbortError(error)) return;
				dispatch({ error, type: SEARCH_PRODUCTS.ERROR, payload });
			});
	};
}

export function searchUnified(
	filters: SearchFilters,
	searchOptions: SearchOptions = { clearPreviousResults: false },
	abortSignal?: AbortSignal
): ThunkAction<SearchUnifiedAction> {
	return async (dispatch, getState) => {
		let request: SearchRequest;
		let payload: { filters: SearchFilters; pagination: Pagination };
		const pagination = searchOptions.pagination || getState().search.unified.pagination;

		if (shouldFetchNextResults(filters, getState().search.searchFilters, searchOptions)) {
			if (!hasNext(getState().search.unified.pagination)) {
				return;
			}
			request = {
				...getState().search.searchFilters,
				offset: pagination.offset,
				limit: pagination.limit,
				excludePublishers: searchOptions.excludePublishers,
				scoreAggregationMethod: searchOptions.scoreAggregationMethod,
				experiments: searchOptions.experiments,
				useAiFilters: searchOptions.useAiFilters,
			};
			payload = {
				filters: getState().search.searchFilters || defaultSearchFilters,
				pagination: pagination,
			};
		} else {
			pagination.offset = 0;
			request = {
				...filters,
				offset: pagination.offset,
				limit: pagination.limit,
				excludePublishers: searchOptions.excludePublishers,
				scoreAggregationMethod: searchOptions.scoreAggregationMethod,
				experiments: searchOptions.experiments,
				useAiFilters: searchOptions.useAiFilters,
			};
			payload = { filters, pagination };
		}

		request.contentType = defaultSearchFilters.contentType;

		dispatch({ type: SEARCH_UNIFIED.START, payload: { ...payload, searchId: getCurrentSearchId() } });

		services.search
			.unifiedSearch(request, abortSignal)
			.then((result) => {
				dispatch({ type: SEARCH_UNIFIED.SUCCESS, result, payload });
			})
			.catch((error) => {
				if (isAbortError(error)) return;
				dispatch({ error, type: SEARCH_UNIFIED.ERROR, payload });
			});
	};
}

export function setResultsSearchId({
	searchId,
	isCourse,
}: {
	searchId: string;
	isCourse: boolean;
}): ThunkAction<SetResultsSearchIdAction> {
	return async (dispatch) => {
		dispatch({ type: SET_RESULTS_SEARCH_ID, payload: { searchId, isCourse } });
	};
}

export function searchTopics(subject?: string): ThunkAction<SearchTopicsAction> {
	return async (dispatch, getState) => {
		if (!subject) {
			return;
		}
		const payload = { subject };
		if (getState().search.subTopics.terms[subject]) {
			return;
		}
		dispatch({ type: SEARCH_TOPICS.START, payload });
		services.mindItem
			.getTopics(subject)
			.then((result) => {
				dispatch({ type: SEARCH_TOPICS.SUCCESS, result, payload });
			})
			.catch((error) => {
				dispatch({ error, type: SEARCH_TOPICS.ERROR, payload });
			});
	};
}

export function fetchWorkloads(): ThunkAction<FetchWorkloadsAction> {
	return async (dispatch, getState) => {
		if (getState().search.workloads.status === AsyncActionStatus.READY) {
			return;
		}

		dispatch({ type: FETCH_WORKLOADS.START });

		return services.text
			.fetchWorkloads()
			.then((result) => dispatch({ type: FETCH_WORKLOADS.SUCCESS, result }))
			.catch((error) => dispatch({ type: FETCH_WORKLOADS.ERROR, error }));
	};
}

export function fetchTeachingFormats(): ThunkAction<FetchTeachingFormatsAction> {
	return async (dispatch, getState) => {
		if (getState().search.teachingFormats.status === AsyncActionStatus.READY) {
			return;
		}

		dispatch({ type: FETCH_TEACHING_FORMATS.START });

		return services.text
			.fetchTeachingFormats()
			.then((result) => dispatch({ type: FETCH_TEACHING_FORMATS.SUCCESS, result }))
			.catch((error) => dispatch({ type: FETCH_TEACHING_FORMATS.ERROR, error }));
	};
}

const shouldFetchNextResults = (
	filters: SearchFilters,
	searchFilters: SearchFilters | undefined,
	options: SearchOptions
) => {
	return _.isEqual(filters, searchFilters) && !options.clearPreviousResults;
};
